// TrackOrder.js
import { useState } from "react";
import apiClient from "../utils/apiClient";
import { trackOrder } from "../utils/APIRoutes";
import { toast } from "react-hot-toast";

// DESIGN NOTE (not a change from before, just making it explicit):
// this deliberately has no auth check at all. A buyer at checkout
// isn't required to have an account (see /api/checkout in app.js —
// guest checkout), so the order-code + checkout-email pair is the
// actual credential here, not a session. That means this component
// correctly serves both an anonymous buyer AND a signed-in one
// identically — apiClient will attach a session cookie if one exists,
// but the backend route (/api/orders/track/:orderCode) never reads
// req.userId, so a logged-in visitor's cookie is simply along for the
// ride and changes nothing. Swapped from `fetch` to `apiClient` only
// for consistency with the rest of the app, not because this route
// needs credentials.
const TrackOrder = () => {
  const [orderCode, setOrderCode] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOrder(null);
    try {
      const { data } = await apiClient.get(
        `${trackOrder}/${orderCode.trim().toUpperCase()}?email=${encodeURIComponent(email.trim())}`
      );
      if (!data.success) {
        toast.error(data.message || "Order not found");
        return;
      }
      setOrder(data.order);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Something went wrong looking up your order");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmItem = async (itemId) => {
    setConfirmingId(itemId);
    try {
      const { data } = await apiClient.put(`${trackOrder}/${order.orderCode}/confirm-item`, {
        itemId,
        email: email.trim(),
      });
      if (!data.success) {
        toast.error(data.message || "Failed to confirm item");
        return;
      }
      setOrder(data.order);
      toast.success("Marked as received — thanks!");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Something went wrong");
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: "40px auto", padding: "0 16px" }}>
      <h2 style={{ marginBottom: 4 }}>Track Your Order</h2>
      <p style={{ color: "#666", fontSize: 14, marginTop: 0 }}>
        Enter the code we emailed you along with your checkout email.
      </p>

      <form onSubmit={handleLookup} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <input
          placeholder="Order code (e.g. 7K3PQ9XZ)"
          value={orderCode}
          onChange={(e) => setOrderCode(e.target.value)}
          style={{ padding: 10, borderRadius: 8, border: "1px solid #ddd", textTransform: "uppercase" }}
          required
        />
        <input
          placeholder="Email used at checkout"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ padding: 10, borderRadius: 8, border: "1px solid #ddd" }}
          required
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: 10,
            borderRadius: 8,
            border: "none",
            fontWeight: 700,
            color: "#fff",
            background: "linear-gradient(135deg, #0f1f3d, #16305c, #f97316)",
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Looking up…" : "Find My Order"}
        </button>
      </form>

      {order && (
        <div style={{ marginTop: 24 }}>
          <p style={{ fontSize: 13, color: "#666" }}>
            Status: <strong style={{ textTransform: "capitalize" }}>{order.status}</strong>
          </p>

          {order.cartItems.map((item) => (
            <div
              key={item._id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0",
                borderBottom: "1px solid #eee",
              }}
            >
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{item.name}</p>
                <p style={{ margin: 0, fontSize: 12, color: "#666" }}>
                  {item.quantity} pcs · {item.currency || "Tzs"} {item.price?.toLocaleString()}
                </p>
              </div>

              {item.rejected ? (
                <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 700 }}>Rejected</span>
              ) : item.fundsReleased ? (
                <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 700 }}>Completed</span>
              ) : item.receivedByBuyer ? (
                <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>Confirmed — awaiting release</span>
              ) : (
                <button
                  onClick={() => handleConfirmItem(item._id)}
                  disabled={confirmingId === item._id}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 20,
                    border: "none",
                    color: "#fff",
                    background: "#0f1f3d",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {confirmingId === item._id ? "…" : "Confirm Received"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrackOrder;
